import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import budgetExternalCostsApi, { BudgetExternalPosition, BudgetUnit } from '../../api/budgetExternalCostsApi'
import { RootState } from '../rootState'

export interface ExternalCostsState {
  positions: BudgetExternalPosition[]
  units: BudgetUnit[]
  originalPositions: BudgetExternalPosition[]
  loading: boolean
  saving: boolean
  error: string | null
  hasUnsavedChanges: boolean
  touchedFields: { [key: string]: boolean } // Track which fields have been touched
}

const initialState: ExternalCostsState = {
  positions: [],
  units: [],
  originalPositions: [],
  loading: false,
  saving: false,
  error: null,
  hasUnsavedChanges: false,
  touchedFields: {}
}

// Async thunks
export const fetchExternalPositions = createAsyncThunk(
  'externalCosts/fetchPositions',
  async () => {
    const positions = await budgetExternalCostsApi.getExternalPositions()
    return positions
  }
)

export const fetchUnits = createAsyncThunk(
  'externalCosts/fetchUnits',
  async () => {
    const units = await budgetExternalCostsApi.getUnits()
    return units
  }
)

export const saveExternalPositions = createAsyncThunk(
  'externalCosts/savePositions',
  async (_, { getState }) => {
    const state = getState() as RootState
    const { positions, originalPositions } = state.externalCosts
    
    // Separate new, updated, and deleted positions
    const newPositions = positions.filter(p => !p.id || p.id < 0)
    const updatedPositions = positions.filter(p => {
      if (!p.id || p.id < 0) return false
      const original = originalPositions.find(op => op.id === p.id)
      return original && JSON.stringify(original) !== JSON.stringify(p)
    })
    const deletedIds = originalPositions
      .filter(op => !positions.find(p => p.id === op.id))
      .map(op => op.id!)
    
    const promises: Promise<any>[] = []
    
    // Handle new positions
    if (newPositions.length > 0) {
      const dataToCreate = newPositions.map(p => ({
        ...p,
        id: undefined // Remove temporary IDs
      }))
      promises.push(budgetExternalCostsApi.bulkCreateExternalPositions(dataToCreate))
    }
    
    // Handle updated positions
    if (updatedPositions.length > 0) {
      promises.push(budgetExternalCostsApi.bulkUpdateExternalPositions(updatedPositions))
    }
    
    // Handle deleted positions
    if (deletedIds.length > 0) {
      promises.push(budgetExternalCostsApi.bulkDeleteExternalPositions(deletedIds))
    }
    
    await Promise.all(promises)
    
    // Refetch to get the latest data
    return await budgetExternalCostsApi.getExternalPositions()
  }
)

export const deleteExternalPosition = createAsyncThunk(
  'externalCosts/deletePosition',
  async (position: { id?: number; index: number }) => {
    if (position.id && position.id > 0) {
      await budgetExternalCostsApi.deleteExternalPosition(position.id)
    }
    return position
  }
)

// Slice
const externalCostsSlice = createSlice({
  name: 'externalCosts',
  initialState,
  reducers: {
    addPosition: (state) => {
      const newPosition: BudgetExternalPosition = {
        id: -Date.now(), // Temporary negative ID
        position_name: '',
        supplier_name: '',
        unit: 0,
        price_per_unit: 0
      }
      state.positions.push(newPosition)
      state.hasUnsavedChanges = true
    },
    updatePosition: (state, action: PayloadAction<{ index: number; field: keyof BudgetExternalPosition; value: any }>) => {
      const { index, field, value } = action.payload
      if (state.positions[index]) {
        (state.positions[index] as any)[field] = value
        state.hasUnsavedChanges = true
      }
    },
    removePosition: (state, action: PayloadAction<number>) => {
      state.positions = state.positions.filter((_, index) => index !== action.payload)
      state.hasUnsavedChanges = true
      // Remove touched fields for this position
      Object.keys(state.touchedFields).forEach(key => {
        if (key.startsWith(`${action.payload}-`)) {
          delete state.touchedFields[key]
        }
      })
    },
    resetChanges: (state) => {
      state.positions = [...state.originalPositions]
      state.hasUnsavedChanges = false
      state.touchedFields = {}
    },
    setFieldTouched: (state, action: PayloadAction<{ index: number; field: string }>) => {
      const { index, field } = action.payload
      state.touchedFields[`${index}-${field}`] = true
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch positions
      .addCase(fetchExternalPositions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchExternalPositions.fulfilled, (state, action) => {
        state.loading = false
        state.positions = action.payload
        state.originalPositions = [...action.payload]
        state.hasUnsavedChanges = false
        state.touchedFields = {}
      })
      .addCase(fetchExternalPositions.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch external positions'
      })
      // Fetch units
      .addCase(fetchUnits.pending, (state) => {
        state.error = null
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.units = action.payload
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch units'
      })
      // Save positions
      .addCase(saveExternalPositions.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(saveExternalPositions.fulfilled, (state, action) => {
        state.saving = false
        state.positions = action.payload
        state.originalPositions = [...action.payload]
        state.hasUnsavedChanges = false
        state.touchedFields = {}
      })
      .addCase(saveExternalPositions.rejected, (state, action) => {
        state.saving = false
        state.error = action.error.message || 'Failed to save external positions'
      })
      // Delete position
      .addCase(deleteExternalPosition.pending, (state) => {
        state.error = null
      })
      .addCase(deleteExternalPosition.fulfilled, (state, action) => {
        const { id, index } = action.payload
        if (id && id > 0) {
          // Real position deleted from backend
          state.positions = state.positions.filter(p => p.id !== id)
          state.originalPositions = state.originalPositions.filter(p => p.id !== id)
        } else {
          // New position (not saved yet) removed from local state
          state.positions = state.positions.filter((_, i) => i !== index)
        }
        state.hasUnsavedChanges = JSON.stringify(state.positions) !== JSON.stringify(state.originalPositions)
      })
      .addCase(deleteExternalPosition.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete external position'
      })
  }
})

export const {
  addPosition,
  updatePosition,
  removePosition,
  resetChanges,
  setFieldTouched,
  clearError
} = externalCostsSlice.actions

// Validation helper
const validatePosition = (position: BudgetExternalPosition): string[] => {
  const errors: string[] = []
  
  if (!position.position_name || position.position_name.trim() === '') {
    errors.push('Position name is required')
  }
  
  if (!position.unit || position.unit === 0) {
    errors.push('Unit selection is required')
  }
  
  if (position.price_per_unit === undefined || position.price_per_unit === null || position.price_per_unit === '') {
    errors.push('Unit price is required')
  } else if (Number(position.price_per_unit) < 0) {
    errors.push('Unit price must be a positive number')
  }
  
  return errors
}

// Selectors
export const selectExternalPositions = (state: RootState) => state.externalCosts.positions
export const selectUnits = (state: RootState) => state.externalCosts.units
export const selectIsLoading = (state: RootState) => state.externalCosts.loading
export const selectIsSaving = (state: RootState) => state.externalCosts.saving
export const selectError = (state: RootState) => state.externalCosts.error
export const selectHasUnsavedChanges = (state: RootState) => state.externalCosts.hasUnsavedChanges

// Selector to check if all positions are valid
export const selectIsFormValid = (state: RootState): boolean => {
  const positions = state.externalCosts.positions
  
  if (positions.length === 0) {
    return true // Empty form is valid
  }
  
  return positions.every(position => validatePosition(position).length === 0)
}

// Selector to get validation errors only for touched fields
export const selectValidationErrors = (state: RootState): { [key: number]: string[] } => {
  const positions = state.externalCosts.positions
  const touchedFields = state.externalCosts.touchedFields
  const errors: { [key: number]: string[] } = {}
  
  positions.forEach((position, index) => {
    const fieldErrors: string[] = []
    
    // Only validate position if it's been touched
    if (touchedFields[`${index}-position_name`] && (!position.position_name || position.position_name.trim() === '')) {
      fieldErrors.push('Position name is required')
    }
    
    // Only validate unit if it's been touched
    if (touchedFields[`${index}-unit`] && (!position.unit || position.unit === 0)) {
      fieldErrors.push('Unit selection is required')
    }
    
    // Only validate unit_price if it's been touched
    if (touchedFields[`${index}-price_per_unit`]) {
      if (position.price_per_unit === undefined || position.price_per_unit === null || position.price_per_unit === '') {
        fieldErrors.push('Unit price is required')
      } else if (Number(position.price_per_unit) < 0) {
        fieldErrors.push('Unit price must be a positive number')
      }
    }
    
    if (fieldErrors.length > 0) {
      errors[index] = fieldErrors
    }
  })
  
  return errors
}

// Selector to check if a specific field has been touched
export const selectIsFieldTouched = (state: RootState, index: number, field: string): boolean => {
  return state.externalCosts.touchedFields[`${index}-${field}`] || false
}

export default externalCostsSlice.reducer